// src/features/rbac/components/RolesTable.tsx
// -----------------------------------------------------------------------------
// "Roles → Abilities" table with inline create & expandable sub‑rows
// -----------------------------------------------------------------------------
import React, { useState } from 'react';

import ExpandableTable from '../../../../shared/components/ui/ExpandableTable';
import { ColumnDef }     from '../../../../shared/components/ui/MinimalisticTable';
import ConfirmDeleteButton from '../../../../shared/components/ui/ConfirmDeleteButton';
import { useRoles } from '../hooks/useRoles';
import type { RoleIn } from '../api/rbacApi';

// ──────────────────────────────────────────────────────────────────────────────
export default function RolesTable() {
  const {
    data: roles = [],
    isLoading,
    isError,
    error,
    add,
    remove,
  } = useRoles();

  // — local state for the inline “new role” row —
  const [isAdding, setIsAdding] = useState(false);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  
  /*───────────────────────────────*
   *  Table definition
   *───────────────────────────────*/
    const columns: ColumnDef<(typeof roles)[number]>[] = [
    { header: 'ID',           accessor: 'id_role'      },
    { header: 'Key',          accessor: 'role_key'     },
    { header: 'Display Name', accessor: 'display_name' },

    /* —— Description: single-line, capped width —— */
    {
        header: 'Description',
        accessor: 'description',
        Cell: (val: string) => (
        <span
            className="block max-w-[260px] truncate whitespace-nowrap"
            title={val}
        >
            {val}
        </span>
        ),
    },

    /* —— Actions: right-aligned, minimal width —— */
    {
        header: (
        /* right-align the header cell too */
        <div className="flex justify-end pr-2">
            {/* leave empty or add an icon if you like */}
        </div>
        ),
        accessor: '',
        Cell: (_v, row) => (
        <div className="flex justify-end pr-2">
            <ConfirmDeleteButton
            title="role"
            onConfirm={() => remove.mutate(row.role_key)}
            />
        </div>
        ),
    },
    ]

  const handleCreate = () => {
    const payload: RoleIn = {
      role_key: key.trim(),
      display_name: name.trim() || undefined,
      description: desc.trim() || undefined,
    };

    add.mutate(payload, {
      onSuccess: () => {
        setIsAdding(false);
        setKey('');
        setName('');
        setDesc('');
      },
    });
  };

  /*───────────────────────────────*
   *  Loading & error states
   *───────────────────────────────*/
  if (isLoading) return <p className="text-neutral-400">Loading…</p>;
  if (isError) return <p className="text-rose-400">Error: {error?.message}</p>;

  /*───────────────────────────────*
   *  Compose data with optional new‑row
   *───────────────────────────────*/
  const tableData = isAdding
    ? ([
        {
          id_role: 0,
          role_key: '',
          display_name: '',
          description: '',
          __isNew: true,
        },
        ...roles,
      ] as (typeof roles)[number][])
    : roles;

  /*───────────────────────────────*
   *  Render
   *───────────────────────────────*/
  return (
    <ExpandableTable
      columns={columns}
      data={tableData}
      noDataMessage="No roles."
      showSearch
      showNewButton={!isAdding}
      onNew={() => setIsAdding(true)}

      /* —— inline new‑row config —— */
      editableAccessors={['role_key', 'display_name', 'description']}
      newRowValues={{ role_key: key, display_name: name, description: desc }}
      onNewRowChange={(accessor, val) => {
        if (accessor === 'role_key') setKey(String(val));
        if (accessor === 'display_name') setName(String(val));
        if (accessor === 'description') setDesc(String(val));
      }}
      onAcceptNew={handleCreate}
      onCancelNew={() => {
        setIsAdding(false);
        setKey('');
        setName('');
        setDesc('');
      }}

      /* —— expandable config —— */
      rowKey={(r) => r.id_role}
      renderSubRow={(r) =>
        /* Lazy‑load to avoid import cycle */
        React.createElement(
          require('./RoleAbilities').default,
          { roleKey: r.role_key },
        )
      }
    />
  );
}
